/**
 * Pure helpers for microphone selection (the browser-facing hook is
 * `hooks/use-audio-input-devices.ts`).
 */

export interface AudioInputDevice {
  deviceId: string
  label: string
}

export type MicPermissionState = "unknown" | "prompt" | "granted" | "denied"

/** `null` = let the browser pick the system default input. */
export const SYSTEM_DEFAULT_DEVICE_ID = null

export const MIC_DEVICE_ID_STORAGE_KEY = "vxannotate.voiceNote.micDeviceId"
export const MIC_DEVICE_LABEL_STORAGE_KEY = "vxannotate.voiceNote.micLabel"

/**
 * Turn `enumerateDevices()` output into the list the panel shows. Before the
 * microphone permission is granted browsers return devices with empty labels
 * and ids, which are useless for selection, so they are dropped.
 */
export function toAudioInputDevices(
  devices: ReadonlyArray<Pick<MediaDeviceInfo, "kind" | "deviceId" | "label">>
): AudioInputDevice[] {
  const seen = new Set<string>()
  const out: AudioInputDevice[] = []
  for (const device of devices) {
    if (device.kind !== "audioinput" || !device.deviceId || seen.has(device.deviceId)) continue
    seen.add(device.deviceId)
    out.push({ deviceId: device.deviceId, label: device.label || `Microphone ${out.length + 1}` })
  }
  return out
}

/**
 * Resolve the device to record with: the saved id when it is still present,
 * else a device with the saved label (Safari rotates ids between sessions),
 * else the system default.
 */
export function pickAudioInputDevice(
  devices: readonly AudioInputDevice[],
  savedId: string | null,
  savedLabel: string | null
): string | null {
  if (savedId && devices.some((d) => d.deviceId === savedId)) return savedId
  if (savedLabel) {
    const byLabel = devices.find((d) => d.label === savedLabel)
    if (byLabel) return byLabel.deviceId
  }
  return SYSTEM_DEFAULT_DEVICE_ID
}

/** Map a `getUserMedia` rejection to the permission state it implies. */
export function permissionStateFromError(error: unknown): MicPermissionState {
  const name = error instanceof Error ? error.name : ""
  if (name === "NotAllowedError" || name === "SecurityError") return "denied"
  return "unknown"
}

export function describeMicError(error: unknown): string {
  const name = error instanceof Error ? error.name : ""
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "Microphone access is blocked. Allow it in the browser's site settings, then reload."
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") return "No microphone was found."
  if (name === "NotReadableError" || name === "AbortError") {
    return "The microphone is in use by another application or could not be started."
  }
  if (name === "OverconstrainedError") return "The selected microphone is no longer available."
  return error instanceof Error && error.message ? error.message : "Could not access the microphone."
}
